import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { ModuloPermissaoDashboard, PERMISSAO_TOTAL, SEM_PERMISSAO } from '../../models/dashboard/modulo-permissao.model';
import { UsuarioContextoDTO, PrivilegioDTO } from '../../models/usuario.model';

// Enums de janela de menu conforme o backend (JanelaDeMenu)
const JANELA_VENDAS = 'VENDAS';
// Correcao: o backend usa COMPRA (singular), nao COMPRAS
const JANELA_COMPRAS = 'COMPRA';
const JANELA_ITENS = 'ITENS';
const JANELA_COLABORADORES = 'COLABORADORES';
const JANELA_CLIENTES = 'CLIENTES';
const JANELA_FORNECEDORES = 'FORNECEDORES';
const JANELA_CONFIGURACOES = 'CONFIGURACOES';
const JANELA_CONTA = 'CONTA';
const JANELA_GASTOS = 'GASTOS';
// Correcao: o backend usa RECEBIMENTOS, nao RECEITAS
const JANELA_RECEITAS = 'RECEBIMENTOS';
const JANELA_DASHBOARD = 'DASHBOARD';
// 12 novas janelas (alinhadas com mobile)
const JANELA_GRUPO = 'GRUPO';
const JANELA_SUBGRUPO = 'SUBGRUPO';
const JANELA_MARCA = 'MARCA';
const JANELA_UNIDADE_DE_ITEM = 'UNIDADE_DE_ITEM';
const JANELA_EXTRATO_DE_CONTA = 'EXTRATO_DE_CONTA';
const JANELA_RESUMO_DE_CONTA = 'RESUMO_DE_CONTA';
const JANELA_TRANSFERENCIAS = 'TRANSFERENCIAS';
const JANELA_PERMITE_CANCELAR_NOTA = 'PERMITE_CANCELAR_NOTA';
const JANELA_PERMITE_CANCELAR_MOVIMENTO = 'PERMITE_CANCELAR_MOVIMENTO';
const JANELA_CUPOM = 'CUPOM';
const JANELA_ESTOQUE_LIMITADO = 'ESTOQUE_LIMITADO';
const JANELA_FUNCAO_FUNCIONARIO = 'FUNCAO_FUNCIONARIO';
const JANELA_GESTAO_DE_PEDIDOS = 'GESTAO_DE_PEDIDOS';
const JANELA_CONFIGURACAO_MINISHOP = 'CONFIGURACAO_MINISHOP';
// 2 novas janelas (alinhadas com mobile)
const JANELA_INDICACOES = 'INDICACOES';
const JANELA_GESTAO_CONTADOR = 'GESTAO_CONTADOR';
// 5 janelas proprias da aba Ajustes (backend V1.125)
const JANELA_PLANOS = 'PLANOS';
const JANELA_DADOS_DE_COBRANCA = 'DADOS_DE_COBRANCA';
const JANELA_TIMBRADO = 'TIMBRADO';
const JANELA_IMPRESSORAS = 'IMPRESSORAS';
const JANELA_PRIVILEGIOS = 'PRIVILEGIOS';
// 9 janelas da aba Informes (backend V1.126)
const JANELA_RELATORIO_VENDAS = 'RELATORIO_VENDAS';
const JANELA_RELATORIO_ITENS_VENDIDOS = 'RELATORIO_ITENS_VENDIDOS';
const JANELA_RELATORIO_LISTA_ITENS = 'RELATORIO_LISTA_ITENS';
const JANELA_RELATORIO_EXTRATO_DE_CAIXA = 'RELATORIO_EXTRATO_DE_CAIXA';
const JANELA_RELATORIO_CONTAS_PAGAS = 'RELATORIO_CONTAS_PAGAS';
const JANELA_RELATORIO_CONTAS_A_PAGAR = 'RELATORIO_CONTAS_A_PAGAR';
const JANELA_RELATORIO_CONTAS_RECEBIDAS = 'RELATORIO_CONTAS_RECEBIDAS';
const JANELA_RELATORIO_CONTAS_A_RECEBER = 'RELATORIO_CONTAS_A_RECEBER';
const JANELA_RELATORIO_DRE = 'RELATORIO_DRE';
// 3 janelas separadas na V1.127 do backend
const JANELA_VISUALIZAR_PEDIDOS = 'VISUALIZAR_PEDIDOS';
const JANELA_CONTAS_A_PAGAR = 'CONTAS_A_PAGAR';
const JANELA_CONTAS_A_RECEBER = 'CONTAS_A_RECEBER';

@Injectable({ providedIn: 'root' })
export class PermissaoDashboardService {

  constructor(private apiService: ApiService) {}

  /**
   * Busca o usuario logado pelo email e calcula o ModuloPermissaoDashboard.
   * Replica a logica do PerfilController.setPrivilegiosUsuario() do mobile.
   */
  buscarPermissoes(email: string): Observable<ModuloPermissaoDashboard> {
    return this.apiService.read<any>(`/usuario/email?email=${encodeURIComponent(email)}`).pipe(
      map((response: any) => {
        const conteudo: UsuarioContextoDTO[] = response?.content || [];
        const usuario = conteudo.find(u => u.email === email) || conteudo[0];
        if (!usuario) {
          return PERMISSAO_TOTAL;
        }
        return this.calcularPermissoes(usuario);
      }),
      catchError(() => of(PERMISSAO_TOTAL))
    );
  }

  /**
   * Calcula permissoes com base nos privilegios do grupo do usuario.
   * Admin tem permissao total.
   * Replica ModuloDePermissao.setPrivilegiosUsuario() do mobile.
   */
  calcularPermissoes(usuario: UsuarioContextoDTO): ModuloPermissaoDashboard {
    // Admin tem permissao total (igual ao mobile: categoriaAdmin => tudo true)
    if (usuario.categoriaAdmin === true) {
      return { ...PERMISSAO_TOTAL };
    }

    const permissao: ModuloPermissaoDashboard = { ...SEM_PERMISSAO };

    const privilegios: PrivilegioDTO[] = usuario.grupoDePrivilegio?.privilegios || [];

    for (const priv of privilegios) {
      if (!priv.consulta) continue;
      // O backend serializa janelaDeMenu como string (ex: "VENDAS") ou como objeto { enum: string }
      const janelaRaw = priv.janelaDeMenu;
      const janela = typeof janelaRaw === 'string'
        ? janelaRaw.toUpperCase()
        : janelaRaw.enum.toUpperCase();
      switch (janela) {
        case JANELA_DASHBOARD:     permissao.dashboard = true; break;
        case JANELA_VENDAS:        permissao.pontoDeVenda = true; break;
        case JANELA_COMPRAS:       permissao.compra = true; break;
        case JANELA_ITENS:         permissao.produto = true; break;
        case JANELA_COLABORADORES: permissao.funcionario = true; break;
        case JANELA_CLIENTES:      permissao.cliente = true; break;
        case JANELA_FORNECEDORES:  permissao.fornecedor = true; break;
        case JANELA_CONFIGURACOES: permissao.configuracao = true; break;
        case JANELA_CONTA:         permissao.conta = true; break;
        case JANELA_GASTOS:        permissao.gastos = true; break;
        case JANELA_RECEITAS:      permissao.receitas = true; break;
        case JANELA_GRUPO:                      permissao.grupo = true; break;
        case JANELA_SUBGRUPO:                   permissao.subgrupo = true; break;
        case JANELA_MARCA:                      permissao.marca = true; break;
        case JANELA_UNIDADE_DE_ITEM:            permissao.unidade = true; break;
        case JANELA_EXTRATO_DE_CONTA:           permissao.extratoConta = true; break;
        case JANELA_RESUMO_DE_CONTA:            permissao.resumoConta = true; break;
        case JANELA_TRANSFERENCIAS:             permissao.transferencias = true; break;
        case JANELA_PERMITE_CANCELAR_NOTA:      permissao.cancelarNota = true; break;
        case JANELA_PERMITE_CANCELAR_MOVIMENTO: permissao.cancelarMovimento = true; break;
        case JANELA_CUPOM:                      permissao.cupom = true; break;
        case JANELA_ESTOQUE_LIMITADO:           permissao.estoqueLimitado = true; break;
        case JANELA_FUNCAO_FUNCIONARIO:         permissao.funcaoFuncionario = true; break;
        case JANELA_GESTAO_DE_PEDIDOS:         permissao.gestaoDePedidos = true; break;
        case JANELA_CONFIGURACAO_MINISHOP:     permissao.configuracaoMinishop = true; break;
        case JANELA_INDICACOES:                permissao.indicacoes = true; break;
        case JANELA_GESTAO_CONTADOR:           permissao.gestaoContador = true; break;
        case JANELA_PLANOS:                    permissao.planos = true; break;
        case JANELA_DADOS_DE_COBRANCA:         permissao.dadosDeCobranca = true; break;
        case JANELA_TIMBRADO:                  permissao.timbrado = true; break;
        case JANELA_IMPRESSORAS:               permissao.impressoras = true; break;
        case JANELA_PRIVILEGIOS:               permissao.privilegios = true; break;
        case JANELA_RELATORIO_VENDAS:                      permissao.relatorioVendas = true; break;
        case JANELA_RELATORIO_ITENS_VENDIDOS:              permissao.relatorioItensVendidos = true; break;
        case JANELA_RELATORIO_LISTA_ITENS:                 permissao.relatorioListaItens = true; break;
        case JANELA_RELATORIO_EXTRATO_DE_CAIXA:            permissao.relatorioExtratoDeCaixa = true; break;
        case JANELA_RELATORIO_CONTAS_PAGAS:                permissao.relatorioContasPagas = true; break;
        case JANELA_RELATORIO_CONTAS_A_PAGAR:              permissao.relatorioContasAPagar = true; break;
        case JANELA_RELATORIO_CONTAS_RECEBIDAS:            permissao.relatorioContasRecebidas = true; break;
        case JANELA_RELATORIO_CONTAS_A_RECEBER:            permissao.relatorioContasAReceber = true; break;
        case JANELA_RELATORIO_DRE:                         permissao.relatorioDre = true; break;
        case JANELA_VISUALIZAR_PEDIDOS:                    permissao.visualizarPedidos = true; break;
        case JANELA_CONTAS_A_PAGAR:                        permissao.contasAPagar = true; break;
        case JANELA_CONTAS_A_RECEBER:                      permissao.contasAReceber = true; break;
      }
    }

    return permissao;
  }
}
